/**
 * Utility functions for concierge app
 */
import DOMPurify from 'dompurify';

// Configure DOMPurify to allow safe HTML elements used in our content
// This prevents XSS while preserving formatting from TipTap editor and markdown
const DOMPURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'p',
    'br',
    'hr',
    'strong',
    'em',
    'b',
    'i',
    'u',
    's',
    'strike',
    'ul',
    'ol',
    'li',
    'blockquote',
    'pre',
    'code',
    'a',
    'img',
    'sup',
    'sub',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'div',
    'span',
    'svg',
    'path', // For inline icons (YouTube play button)
    'figure',
    'figcaption', // For image captions (Unsplash attribution)
    'iframe', // For YouTube embeds (filtered by hook below)
  ],
  ALLOWED_ATTR: [
    'href',
    'src',
    'alt',
    'title',
    'target',
    'rel',
    'class',
    'id',
    'style',
    'width',
    'height',
    'loading',
    // SVG attributes
    'viewBox',
    'fill',
    'd',
    'xmlns',
    // iframe attributes (for YouTube embeds)
    'allow',
    'allowfullscreen',
    'frameborder',
    // TipTap data attributes
    'data-youtube-video',
    'data-align',
    'data-width',
    // Unsplash image attribution (hover overlay)
    'data-photographer',
    'data-photographer-url',
  ],
  // Allow data: URLs for inline SVG placeholders
  ALLOW_DATA_ATTR: false,
  ADD_ATTR: ['target'], // Allow target="_blank" for links
};

// Hook to filter iframes - only allow YouTube embeds for security
DOMPurify.addHook('uponSanitizeElement', (node, data) => {
  if (data.tagName === 'iframe') {
    const src = node.getAttribute('src') || '';
    // Only allow YouTube embed URLs
    if (
      !src.startsWith('https://www.youtube.com/embed/') &&
      !src.startsWith('https://youtube.com/embed/')
    ) {
      node.remove();
    }
  }
});

/**
 * Hash-based color generation (matching Python implementation)
 * Creates deterministic colors from strings for placeholder images
 */
export function stringToColor(str) {
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Convert to RGB
  let r = (hash & 0xff0000) >> 16;
  let g = (hash & 0x00ff00) >> 8;
  let b = hash & 0x0000ff;

  // Adjust brightness - not too dark, not too light
  const sum = r + g + b;
  if (sum < 150) {
    r = Math.min(255, r + 100);
    g = Math.min(255, g + 100);
    b = Math.min(255, b + 100);
  } else if (sum > 600) {
    r = Math.max(0, r - 100);
    g = Math.max(0, g - 100);
    b = Math.max(0, b - 100);
  }

  // Convert to hex
  return ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

/**
 * Extract meaningful initials from text
 * Filters out non-alphabetic words like "|", "-", etc.
 */
export function extractInitials(text) {
  if (!text) return '?';

  // Split by spaces and filter to only words containing letters
  const words = text
    .trim()
    .split(/\s+/)
    .filter((word) => /[a-zA-Z]/.test(word));

  if (words.length >= 2) {
    // Get first letter of first two words
    return (words[0][0] + words[1][0]).toUpperCase();
  } else if (words.length === 1 && words[0].length >= 2) {
    // Single word: take first 2 characters
    return words[0].substring(0, 2).toUpperCase();
  } else if (words.length === 1) {
    // Single word, single char
    return words[0][0].toUpperCase();
  } else {
    // No valid words found
    return '?';
  }
}

/**
 * Generate placeholder image as inline SVG data URL
 * More reliable than external services like placehold.co
 */
export function generatePlaceholder(url, title, type = 'article') {
  const color = stringToColor(url);
  let text;

  if (type === 'pdf' || type === 'document') {
    text = 'PDF';
  } else {
    text = extractInitials(title);
  }

  // Generate inline SVG with initials
  const svg = `
    <svg width="200" height="200" xmlns="http://www.w3.org/2000/svg">
      <rect width="200" height="200" fill="#${color}"/>
      <text x="50%" y="50%" font-family="system-ui, -apple-system, sans-serif"
            font-size="72" font-weight="600" fill="#ffffff"
            text-anchor="middle" dominant-baseline="middle">
        ${text}
      </text>
    </svg>
  `.trim();

  // Convert to data URL
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

/**
 * Convert a Loom share URL or video id into an embeddable URL.
 */
export function getLoomEmbedUrl(url) {
  if (!url) return null;
  const normalized = String(url).trim();
  if (!normalized) return null;
  if (normalized.includes('/embed/')) return normalized;
  const match = normalized.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  if (match) return `https://www.loom.com/embed/${match[1]}`;
  if (/^[a-zA-Z0-9]+$/.test(normalized)) return `https://www.loom.com/embed/${normalized}`;
  return normalized;
}

/**
 * Strip markdown syntax from text, returning plain text
 * Use for titles, names, and other places where plain text is expected
 *
 * @param {string} text - The markdown text to strip
 * @returns {string} Plain text with markdown removed
 */
export function stripMarkdown(text) {
  if (!text) return '';

  return (
    text
      // Remove headers (## Header)
      .replace(/^#{1,6}\s+/gm, '')
      // Remove bold/italic (**text**, *text*, __text__, _text_)
      .replace(/(\*\*|__)(.*?)\1/g, '$2')
      .replace(/(\*|_)(.*?)\1/g, '$2')
      // Remove links [text](url) -> text (or just remove if empty text)
      .replace(/\[([^\]]*)\]\([^)]+\)/g, '$1')
      // Remove inline code `code`
      .replace(/`([^`]+)`/g, '$1')
      // Remove images ![alt](url)
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, '')
      // Remove horizontal rules
      .replace(/^(---|\*\*\*|___)$/gm, '')
      // Clean up multiple spaces/newlines
      .replace(/\s+/g, ' ')
      .trim()
  );
}

/**
 * Strip HTML tags from text, returning plain text
 * Use for previews, summaries, and other places where plain text is expected
 *
 * @param {string} html - The HTML text to strip
 * @returns {string} Plain text with HTML tags removed
 */
export function stripHtml(html) {
  if (!html) return '';

  return (
    html
      // Remove HTML tags
      .replace(/<[^>]+>/g, ' ')
      // Decode common HTML entities
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'")
      // Clean up multiple spaces/newlines
      .replace(/\s+/g, ' ')
      .trim()
  );
}

/**
 * Parse markdown to HTML
 * Supports: headings (h1-h4), horizontal rules (---), **bold**, *italic*, [links](url), plain URLs, YouTube embeds, citations, and line breaks
 * Also handles content that is already HTML (from Tiptap WYSIWYG editor)
 *
 * @param {string} text - The markdown text to parse
 * @param {Array} citationsArray - Optional array of citation objects with chunk_id field
 * @param {boolean} skipMarkdownParsing - If true, treat content as HTML and skip markdown processing (for curated answers)
 */
export function parseMarkdown(text, citationsArray = null, skipMarkdownParsing = false) {
  if (!text) return '';

  // Defensive cleanup: if raw inline citation tokens leak into prose, strip them
  // and rely on the structured citations UI below instead of rendering the token.
  const leakedInlineCitationPattern =
    /(?:\s*【[^】]*(?:chunk_[^】]*|curated_[^】]*)】|\s*[▮█▌▋■▐▏▎▍]+(?:cite[▮█▌▋■▐▏▎▍]+)?[a-z0-9_-]*(?:chunk_[a-z0-9_-]+|curated_[a-z0-9_-]+)[▮█▌▋■▐▏▎▍]*)/gi;
  text = text.replace(leakedInlineCitationPattern, '');

  // If explicitly told to skip markdown parsing, or if content looks like HTML
  // HTML content typically starts with a tag like <h2>, <p>, <strong>, etc.
  const isHtml =
    skipMarkdownParsing ||
    /^<[a-z][^>]*>/i.test(text.trim()) ||
    /<(h[1-6]|p|strong|em|ul|ol|li|blockquote|a|img)\b/i.test(text);

  if (isHtml) {
    // Content is already HTML (from Tiptap curated answers)
    // Wrap in styled container and process citations
    let html = text;

    // Handle citations in HTML content (backend may still use citation markers)
    const backendCitationPattern = /⟪CITATION:::(\?|UNKNOWN):::([^⟫]+)⟫/gi;
    html = html.replace(backendCitationPattern, (_match, _placeholder, chunkId) => {
      if (citationsArray && citationsArray.length > 0) {
        const citationIndex = citationsArray.findIndex((c) => c.chunk_id === chunkId);
        if (citationIndex !== -1) {
          const citationNumber = citationIndex + 1;
          return `<sup><a href="#source-${citationNumber}" class="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-full ml-0.5 cursor-pointer hover:bg-indigo-100 hover:ring-2 hover:ring-indigo-300 transition-all no-underline" title="Jump to source ${citationNumber}">[${citationNumber}]</a></sup>`;
        }
      }
      // No match - remove orphaned citation
      return '';
    });

    // Wrap Unsplash images (with data-photographer attribute) in <figure> for hover overlay
    // This transforms: <img ... data-photographer="Name" data-photographer-url="URL" />
    // Into: <figure class="unsplash-image" data-photographer="Name"><img ... /></figure>
    html = html.replace(
      /<img\s+([^>]*?)data-photographer="([^"]+)"([^>]*?)data-photographer-url="([^"]+)"([^>]*?)\/?>/gi,
      '<figure class="unsplash-image" data-photographer="$2" data-photographer-url="$4"><img $1$3$5/></figure>',
    );
    // Also handle reverse attribute order
    html = html.replace(
      /<img\s+([^>]*?)data-photographer-url="([^"]+)"([^>]*?)data-photographer="([^"]+)"([^>]*?)\/?>/gi,
      '<figure class="unsplash-image" data-photographer="$4" data-photographer-url="$2"><img $1$3$5/></figure>',
    );

    // Wrap in styled container for consistent rendering
    // Sanitize HTML to prevent XSS attacks
    return DOMPurify.sanitize(`<div class="html-content">${html}</div>`, DOMPURIFY_CONFIG);
  }

  let html = text;

  // Step 1: Parse markdown patterns BEFORE escaping HTML
  // Use unique markers that won't appear in normal text

  // Horizontal rules: --- or *** or ___ (must be on their own line)
  html = html.replace(/^(---|\*\*\*|___)$/gm, '⟪HR⟫');

  // Headings: Must be at the start of a line or after a line break
  // H4 (####) must be processed before H3, H3 before H2, etc.
  html = html.replace(/^#### (.+)$/gm, '⟪H4:::$1⟫');
  html = html.replace(/^### (.+)$/gm, '⟪H3:::$1⟫');
  html = html.replace(/^## (.+)$/gm, '⟪H2:::$1⟫');
  html = html.replace(/^# (.+)$/gm, '⟪H1:::$1⟫');

  // YouTube URLs: Convert to preview cards (BEFORE processing regular links)
  // Matches standalone YouTube URLs (not already in markdown link syntax)
  html = html.replace(
    /(?<![\[\(])(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)[a-zA-Z0-9_-]+[^\s<⟪]*)/g,
    (fullMatch) => {
      const videoId = extractYouTubeId(fullMatch);
      if (videoId) {
        return `⟪YOUTUBE:::${videoId}:::${fullMatch}⟫`;
      }
      return fullMatch;
    },
  );

  // Citations: Handle both formats
  // Format 1: Backend-generated ⟪CITATION:::?:::chunk_id⟫ (new format from backend)
  // Format 2: [chunk_id] (legacy format)

  // First, handle backend-generated citations that already have the marker format
  const backendCitationPattern = /⟪CITATION:::(\?|UNKNOWN):::([^⟫]+)⟫/gi;
  html = html.replace(backendCitationPattern, (_match, _placeholder, chunkId) => {
    // If citations array provided, find the index in that array
    if (citationsArray && citationsArray.length > 0) {
      const citationIndex = citationsArray.findIndex((c) => c.chunk_id === chunkId);
      if (citationIndex !== -1) {
        return `⟪CITATION:::${citationIndex + 1}:::${chunkId}⟫`;
      }
    }
    // No match found - remove the orphaned citation reference entirely
    return '';
  });

  // Then handle legacy [chunk_id] format (including multiple chunk IDs in one bracket)
  // Matches: [chunk_id] or [chunk_id1, chunk_id2, ...]
  const citationPattern = /\[([a-z0-9_,\s-]+chunk_\d{4}[a-z0-9_,\s-]*)\]/gi;
  html = html.replace(citationPattern, (_match, chunkIds) => {
    // Split by comma in case there are multiple chunk IDs
    const ids = chunkIds.split(',').map((id) => id.trim());

    // Convert each chunk ID to a citation marker, filtering out unmatched ones
    const citationMarkers = ids
      .map((chunkId) => {
        if (citationsArray && citationsArray.length > 0) {
          const citationIndex = citationsArray.findIndex((c) => c.chunk_id === chunkId);
          if (citationIndex !== -1) {
            return `⟪CITATION:::${citationIndex + 1}:::${chunkId}⟫`;
          }
        }
        // No match - return null to filter out
        return null;
      })
      .filter(Boolean); // Remove nulls

    // Return matched citation markers joined, or empty string if none matched
    return citationMarkers.join('');
  });

  // Links: [text](url) -> temporary marker (do this AFTER citations)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '⟪MDLINK:::$2:::$1⟫');

  // Plain URLs: Convert bare URLs to links (but not ones already in markers)
  // Only match URLs that aren't already inside ⟪ ⟫ markers
  html = html.replace(/(?<!⟪[^⟫]*)(https?:\/\/[^\s<⟪]+[^<.,:;"')\]\s⟪])/g, '⟪PLAINURL:::$1:::$1⟫');

  // Bold: **text** -> temporary start/end markers so nested link/url markers remain valid
  html = html.replace(/\*\*([^*]+)\*\*/g, '⟪BOLD_START⟫$1⟪BOLD_END⟫');

  // Italic: *text* -> temporary start/end markers (single asterisk, not part of **)
  html = html.replace(/\*([^*\n]+)\*/g, '⟪ITALIC_START⟫$1⟪ITALIC_END⟫');

  // Step 2: Escape HTML entities (for security)
  html = html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  // Step 3: Handle line breaks
  html = html.replace(/\n/g, '<br>');

  // Step 4: Replace markers with actual HTML
  // Horizontal rule
  html = html.replace(/⟪HR⟫/g, '<hr class="my-3 border-t border-slate-300">');

  // Headings with proper styling (reduced top margin since <br> already provides spacing)
  html = html.replace(
    /⟪H1:::([^⟫]+)⟫/g,
    '<h1 class="text-2xl font-bold text-slate-900 mt-1 mb-1">$1</h1>',
  );
  html = html.replace(
    /⟪H2:::([^⟫]+)⟫/g,
    '<h2 class="text-xl font-bold text-slate-900 mt-1 mb-1">$1</h2>',
  );
  html = html.replace(
    /⟪H3:::([^⟫]+)⟫/g,
    '<h3 class="text-lg font-semibold text-slate-900 mt-1 mb-0.5">$1</h3>',
  );
  html = html.replace(
    /⟪H4:::([^⟫]+)⟫/g,
    '<h4 class="text-base font-semibold text-slate-900 mt-0.5 mb-0.5">$1</h4>',
  );

  // YouTube preview cards
  html = html.replace(/⟪YOUTUBE:::([^:]+):::([^⟫]+)⟫/g, (match, videoId, url) => {
    const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="block my-3 no-underline group">
      <div class="relative overflow-hidden rounded-lg border border-slate-200 bg-slate-50 hover:border-indigo-300 transition-all max-w-md">
        <div class="relative aspect-video bg-slate-200">
          <img src="${thumbnailUrl}" alt="YouTube video" class="w-full h-full object-cover" loading="lazy" />
          <div class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 group-hover:bg-opacity-30 transition-all">
            <div class="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
              <svg class="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </div>
          </div>
        </div>
        <div class="px-3 py-2 bg-white border-t border-slate-200">
          <div class="flex items-center gap-2 text-xs text-slate-600">
            <svg class="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            <span class="font-medium">Watch on YouTube</span>
          </div>
        </div>
      </div>
    </a>`;
  });

  // Clean up excessive <br> tags after headings (h1-h4) and horizontal rules
  html = html.replace(/(<\/h[1-4]>)<br><br>/g, '$1<br>');
  html = html.replace(/(<hr[^>]*>)<br><br>/g, '$1<br>');
  html = html.replace(/(<\/a>)<br><br>(?=<a[^>]*class="[^"]*block[^"]*my-3)/g, '$1<br>'); // Clean up after YouTube cards

  html = html.replace(/⟪BOLD_START⟫/g, '<strong class="font-semibold text-slate-900">');
  html = html.replace(/⟪BOLD_END⟫/g, '</strong>');
  html = html.replace(/⟪ITALIC_START⟫/g, '<em class="italic text-slate-700">');
  html = html.replace(/⟪ITALIC_END⟫/g, '</em>');
  html = html.replace(
    /⟪MDLINK:::(.+?):::(.+?)⟫/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-indigo-600 hover:text-indigo-700 underline">$2</a>',
  );
  html = html.replace(
    /⟪PLAINURL:::(.+?):::(.+?)⟫/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-indigo-600 hover:text-indigo-700 underline">$2</a>',
  );

  // Citations: Convert to clickable superscript numbered references
  // Clicking [1] scrolls to source-1 below
  // Orphaned citations (no match in citations array) are removed upstream, so only numbered refs remain
  html = html.replace(
    /⟪CITATION:::(\d+):::([^⟫]+)⟫/g,
    '<sup><a href="#source-$1" class="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-full ml-0.5 cursor-pointer hover:bg-indigo-100 hover:ring-2 hover:ring-indigo-300 transition-all no-underline" title="Jump to source $1">[$1]</a></sup>',
  );

  // Sanitize HTML to prevent XSS attacks
  return DOMPurify.sanitize(html, DOMPURIFY_CONFIG);
}

/**
 * Extract domain from URL
 */
export function extractDomain(url) {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace('www.', '');
  } catch (e) {
    return 'External link';
  }
}

/**
 * Detect content type from URL
 */
export function detectContentType(url) {
  const urlLower = url.toLowerCase();

  if (urlLower.endsWith('.pdf')) {
    return 'document';
  } else if (urlLower.includes('youtube.com/watch') || urlLower.includes('youtu.be/')) {
    return 'video';
  } else {
    return 'article';
  }
}

/**
 * Extract YouTube video ID from URL
 * Supports multiple YouTube URL formats
 */
export function extractYouTubeId(url) {
  if (url.includes('youtube.com/watch')) {
    const match = url.match(/[?&]v=([^&]+)/);
    if (match) return match[1];
  } else if (url.includes('youtu.be/')) {
    const match = url.match(/youtu\.be\/([^?&]+)/);
    if (match) return match[1];
  } else if (url.includes('youtube.com/embed/')) {
    const match = url.match(/youtube\.com\/embed\/([^?&]+)/);
    if (match) return match[1];
  }
  return null;
}

/**
 * Check if URL is a YouTube URL
 */
export function isYouTubeUrl(url) {
  return (
    url.includes('youtube.com/watch') ||
    url.includes('youtu.be/') ||
    url.includes('youtube.com/embed/')
  );
}

/**
 * Get client ID from URL path
 * URL format: /{clientId} or /{clientId}/answer/{answerId}
 * Returns null if no client ID is in the path
 */
export function getClientIdFromPath() {
  const path = window.location.pathname;
  const segments = path.split('/').filter(Boolean);

  if (segments.length > 0) {
    return segments[0];
  }

  // No default - return null if client ID not in path
  return null;
}

/**
 * Get answer ID from URL path
 * URL format: /{clientId}/answer/{answerId}
 * Returns null if not on an answer page
 */
export function getAnswerIdFromPath() {
  const path = window.location.pathname;
  const segments = path.split('/').filter(Boolean);

  // Format: /clientId/answer/answerId
  if (segments.length >= 3 && segments[1] === 'answer') {
    return segments[2];
  }

  return null;
}

/**
 * Build URL for a curated answer
 * @param {string} clientId - Client identifier
 * @param {string} answerId - Curated answer ID
 * @returns {string} Full path URL (e.g., /aboncle/answer/curated_xxx)
 */
export function buildAnswerUrl(clientId, answerId) {
  return `/${clientId}/answer/${answerId}`;
}

/**
 * Update browser URL without full page reload
 * @param {string} url - New URL path
 * @param {string} title - Page title (optional)
 */
export function pushUrl(url, title = '') {
  window.history.pushState({}, title, url);
}

/**
 * Replace browser URL without adding to history
 * @param {string} url - New URL path
 * @param {string} title - Page title (optional)
 */
export function replaceUrl(url, title = '') {
  window.history.replaceState({}, title, url);
}

/**
 * Format a date the way the *organisation* would write it, not the visitor.
 *
 * `toLocaleDateString(undefined, …)` reads the browser's locale and the
 * browser's clock, which is wrong twice over for a tenant-facing page: a
 * Brazilian clinic's site rendered "Saturday, September 19, 2026" to anyone
 * whose browser was English, and a visitor in another timezone was shown the
 * wrong day above a page about appointments.
 *
 * Falls back to the browser only when the tenant has declared neither, which
 * is what an unconfigured client looks like — better a date in the wrong
 * language than no date at all.
 */
export function formatOrgDate(date, clientConfig, options = {}) {
  const locale = clientConfig?.locale || undefined;
  const timeZone = clientConfig?.timezone || undefined;
  try {
    return new Date(date).toLocaleDateString(locale, { ...options, timeZone });
  } catch {
    // An invalid locale or zone from configuration must not blank the page.
    return new Date(date).toLocaleDateString(undefined, options);
  }
}
